import { Component, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { ButtonComponent } from "../button/button.component";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FileSelectEvent, FileUploadModule } from "@openng/optimus-ui/fileupload";
import { DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { GateFaultService } from "../../../services/api/gate-fault/gate-fault.service";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { MessageService } from "@openng/optimus-ui/api";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { MAX_SIZE } from "../../../constants/maxSize";
import { first } from "rxjs";
import { TextareaModule } from "@openng/optimus-ui/textarea";

@Component({
  selector: "app-repair-request",
  standalone: true,
  imports: [
    ButtonComponent,
    TranslateModule,
    TextareaModule,
    FileUploadModule,
    ReactiveFormsModule,
    SvgIconComponent,
    ProgressSpinnerModule,
  ],
  templateUrl: "./repair-request.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./repair-request.component.scss",
})
export class RepairRequestComponent {
  private config = inject(DynamicDialogConfig);
  private gateFaultService = inject(GateFaultService);
  private ref = inject(DynamicDialogRef);
  private message = inject(MessageService);
  public translate = inject(TranslateService);

  public form = new FormGroup({
    notes: new FormControl(),
  });
  public files: File[] = [];

  public loading = signal(false);
  public ACCEPTED_FILE_TYPES = ["image/*", "video/*"];

  /**
   * Submits a repair request with the given gate fault id and optional notes and files.
   * Closes the dialog with "request_success" if successful, or "request_error" if not.
   */
  public request() {
    const gateFaultId = this.config.data.gateFaultId;
    this.loading.set(true);
    this.gateFaultService
      .requestRepair(gateFaultId, this.form.value.notes, this.files)
      .pipe(first())
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.ref.close("request_success");
        },
        error: () => {
          this.loading.set(false);
          this.ref.close("request_error");
        },
      });
  }
  /**
   * Closes the dialog with null, indicating that the request was canceled.
   */
  public cancel() {
    this.ref.close(null);
  }
  /**
   * Adds a file to the array of files to be uploaded as media for the repair request, if the file is of an accepted type and size.
   * If the limit of 15 files is exceeded, or if the file is the wrong type/size, shows an error message.
   * @param event The file upload event.
   */
  public onFileSelect(event: FileSelectEvent) {
    if (this.files.length < 15) {
      if (this.checkFileType(event.files[0].type)) {
        if (event.files[0].size < MAX_SIZE) {
          this.files.push(...event.files);
        } else {
          this.message.add({
            key: "global",
            severity: "error",
            summary: this.translate.instant("common.error"),
            detail: this.translate.instant("gate_faults.repair_modal.max_size"),
            life: 5000,
          });
        }
      } else {
        this.message.add({
          key: "global",
          severity: "error",
          summary: this.translate.instant("common.error"),
          detail: this.translate.instant("gate_faults.repair_modal.wrong_type"),
          life: 5000,
        });
      }
    } else {
      this.message.add({
        key: "global",
        severity: "error",
        summary: this.translate.instant("common.error"),
        detail: this.translate.instant("gate_faults.repair_modal.max_images"),
        life: 5000,
      });
    }
  }

  /**
   * Checks if the given file type is among the accepted file types.
   * @param fileType The MIME type of the file to check.
   * @returns True if the file type is accepted, false otherwise.
   */
  private checkFileType(fileType: string) {
    const acceptedFileTypes = this.ACCEPTED_FILE_TYPES.map((type) => type.split("/")[0]);
    const fileTypeSplit = fileType.split("/")[0];

    return acceptedFileTypes.includes(fileTypeSplit);
  }
  /**
   * Deletes a file from the array of files to be uploaded as media for the repair request.
   * @param index The index of the file to be deleted.
   */
  public onDeleteImage(index: number) {
    this.files.splice(index, 1);
  }
}
